/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { NotFoundErrorView } from '../error/ErrorPages.view';
import FlaggedContents from './FlaggedContentList.container';
import { flaggedContentService } from '../../ServiceFactory';
import { FlaggedType } from './store/FlaggedContent.state';
import FlaggedChatContainer from './flagged-content-details/FlaggedChat.container';
import FlaggedCommentContainer from './flagged-content-details/FlaggedComment.container';
import FlaggedPostContainer from './flagged-content-details/FlaggedPost.container';
import FlaggedOtherContainer from './flagged-content-details/FlaggedOther.container';
import FlaggedGroupContainer from './flagged-content-details/FlaggedGroup.container';

export const FLAGGED_CONTENTS_URL = '/flagged-contents';
const FLAGGED_CONTENTS_DETAILS_URL_BASE = FLAGGED_CONTENTS_URL + '/details/:entityType/:id';

export function getFlaggedContentsDetailsUrlWithEntityType(flaggedContentEntityType: string): string {
	return FLAGGED_CONTENTS_DETAILS_URL_BASE.replace(':entityType', flaggedContentEntityType);
}

export function getFlaggedContentsDetailsUrlWithEntityTypeAndId(
	flaggedContentEntityType: string,
	flaggedContentId: string
): string {
	return FLAGGED_CONTENTS_DETAILS_URL_BASE.replace(':entityType', flaggedContentEntityType).replace(
		':id',
		flaggedContentId
	);
}

interface Props extends RouteComponentProps {}

class FlaggedContentRoutingContainer extends React.Component<Props> {
	renderDetails(): JSX.Element {
		return (
			<Switch>
				<Route
					path={getFlaggedContentsDetailsUrlWithEntityType(
						flaggedContentService().getShortEntityTypeName(FlaggedType.CHAT)
					)}
					component={FlaggedChatContainer}
				/>
				<Route
					path={getFlaggedContentsDetailsUrlWithEntityType(
						flaggedContentService().getShortEntityTypeName(FlaggedType.COMMENT)
					)}
					component={FlaggedCommentContainer}
				/>
				<Route
					path={getFlaggedContentsDetailsUrlWithEntityType(
						flaggedContentService().getShortEntityTypeName(FlaggedType.POST)
					)}
					component={FlaggedPostContainer}
				/>
				<Route
					path={getFlaggedContentsDetailsUrlWithEntityType(
						flaggedContentService().getShortEntityTypeName(FlaggedType.GROUP)
					)}
					component={FlaggedGroupContainer}
				/>
				<Route component={FlaggedOtherContainer} />
			</Switch>
		);
	}

	render(): JSX.Element {
		const {
			match: { url }
		} = this.props;

		return (
			<Switch>
				<Route exact path={url} component={FlaggedContents} />
				<Route exact path={FLAGGED_CONTENTS_DETAILS_URL_BASE} render={this.renderDetails} />
				<Route component={NotFoundErrorView} />
			</Switch>
		);
	}
}

export default withRouter(FlaggedContentRoutingContainer);
